/* eslint-disable i18next/no-literal-string */

import * as React from 'react';
import { initReactI18next, UseTranslationResponse, WithTranslation, withTranslation, WithTranslationProps } from 'react-i18next';
import { getLocalesPath } from 'common/utilities/AssetsPath';
import { pageDataService } from 'common/utilities/PageDataService';
import { Configuration } from 'Configuration';
import i18n, { Namespace, PostProcessorModule } from 'i18next';
import Backend from 'i18next-http-backend';

import { localeIgnoreCaseEquals } from './common/utilities/String';

export { useLocalizedStrings } from 'common/hooks/useLocalizedStrings';

export enum DateTimeFormatType {
  ShortDate = 'ShortDate', // 'M/D/YYYY' in en-us locale -> "6/17/2015"  - Default
  LongDateNoYear = 'LongDateNoYear', // 'MMM D' in en-us locale -> "17 Jun"  - Default
  LongDate = 'LongDate', // 'MMM D, YYYY' in en-us locale -> "Jun 17, 2015"
  ShortTime = 'ShortTime', // 'h:mm A' in en-us locale -> "2:24 PM"
  LongTime = 'LongTime', // 'h:mm:ss A' in en-us locale -> "2:24:36 PM"
  FullDateTime = 'FullDateTime', // 'MMM D, YYYY h:mm A' in en-us locale -> "Jun 17, 2015 2:24 AM"
  MonthDayOnly = 'MonthDayOnly', // "MM/DD" in en-us locale -> "06/17"
  MonthYearOnly = 'MonthYearOnly', // "MMMM YYYY" in en-us locale -> "June 2015
  ISO = 'ISO', // ISO format -> "2015-06-17T21:24:36.000Z"
}

export type LocalizationProps = UseTranslationResponse<'translation', undefined>;

export function withLocalization(
  ns?: Namespace,
  options?: {
    withRef?: boolean;
  }
): <P extends WithTranslation<Namespace>>(
  component: React.ComponentType<P>
) => React.ComponentType<Omit<P, keyof WithTranslation & WithTranslationProps>> {
  return withTranslation(ns, options);
}

export function getLocale(): string {
  let locale = pageDataService.getRequestLocale();

  if (!locale || localeIgnoreCaseEquals(locale, 'en-us')) {
    locale = 'en';
  }

  return locale;
}

export function isEnglishUSLocale(): boolean {
  const locale = pageDataService.getRequestLocale();
  return !!locale && localeIgnoreCaseEquals(locale, 'en-us');
}

export function isEnglishLocale(): boolean {
  return localeIgnoreCaseEquals(getLocale(), 'en') || localeIgnoreCaseEquals(getLocale(), 'en-gb');
}

export function shouldMeetFrenchRegulation(): boolean {
  const locale = getLocale().toLocaleLowerCase();
  return localeIgnoreCaseEquals(locale, 'fr') || locale.startsWith('fr-');
}

const PseudoLocPostProcessorName = 'Pseudoloc';
const pseudoLocEnabled = localStorage.getItem('pseudoLocEnabled') === 'true';

const pseudoLocPostProcessor: PostProcessorModule = {
  name: PseudoLocPostProcessorName,
  type: 'postProcessor',
  process(value: string): string {
    if (pseudoLocEnabled) {
      // Add 40% of the length as text.
      const padding = ''.padStart(value.length, 'psuedo padding ');

      // Choose some unicode characters to show in prefix/suffix.
      const prefix = '!\u4e55';
      const suffix = '\u5870!';

      return `${prefix} ${padding} ((${value})) ${padding} ${suffix}`;
    }
    return value;
  },
};

export function getFormatOptions(fmt?: DateTimeFormatType | string | undefined): Intl.DateTimeFormatOptions {
  switch (fmt) {
    case DateTimeFormatType.ShortDate:
      return { year: 'numeric', month: 'numeric', day: 'numeric' };
    case DateTimeFormatType.LongDateNoYear:
      return { month: 'short', day: 'numeric' };
    case DateTimeFormatType.LongDate:
      return { year: 'numeric', month: 'short', day: 'numeric' };
    case DateTimeFormatType.ShortTime:
      return { hour: 'numeric', minute: 'numeric' };
    case DateTimeFormatType.LongTime:
      return { hour: 'numeric', minute: 'numeric', second: 'numeric' };
    case DateTimeFormatType.FullDateTime:
      return { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };
    case DateTimeFormatType.MonthDayOnly:
      return { month: 'numeric', day: 'numeric' };
    case DateTimeFormatType.MonthYearOnly:
      return { month: 'long', year: 'numeric' };
    default:
      return { year: 'numeric', month: 'numeric', day: 'numeric' };
  }
}

const namespaces = ['devices', 'identity', 'dashboard', 'translation', 'alerts', 'credit'];
if (pageDataService.isCopilotEnabled()) {
  namespaces.push('copilot');
}

export function i18nInit(localeOverride = '') {
  void i18n
    .use(initReactI18next)
    .use(Backend)
    .use(pseudoLocPostProcessor)
    .init({
      fallbackLng: 'en',
      ns: namespaces,
      defaultNS: 'translation',
      returnNull: false,
      returnEmptyString: false,
      appendNamespaceToCIMode: true,
      interpolation: {
        escapeValue: false,
        defaultVariables: {
          ProductName: Configuration.ProductName,
          FullProductName: Configuration.FullProductName,
          ExperianName: Configuration.ExperianName,
          AppStoreNames: Configuration.AppStoreNames,
        },
      },
      backend: {
        loadPath: getLocalesPath(),
      },
      react: {
        useSuspense: true,
      },
      load: 'currentOnly',
      lng: localeOverride || getLocale(),
      postProcess: pseudoLocEnabled ? PseudoLocPostProcessorName : false,
    });

  // add custom formats
  Object.values(DateTimeFormatType).forEach((fmt) => {
    i18n.services.formatter?.add(
      fmt.toLowerCase(), // i18next looks for formatter by the lowercase'd format name
      (value, lng) => {
        if (value instanceof Date) {
          return Intl.DateTimeFormat(lng, getFormatOptions(fmt)).format(value);
        }
        return value;
      }
    );
  });
}

export default i18n;

// TODO: refactor usages to use built-in formatting (work item 38046226)
export function formatDateTime(date: Date, format?: DateTimeFormatType | string): string {
  if (format === DateTimeFormatType.ISO) return date.toISOString();
  const options = getFormatOptions(format);
  const locale = i18n.language;
  return Intl.DateTimeFormat(locale, options).format(date);
}

export function formatToRelativeDateTime(date: Date): string {
  const dateNow = new Date(Date.now());
  const isToday = date.toDateString() === dateNow.toDateString();
  const dateYesterday = new Date(Date.now() - 1000 * 60 * 60 * 24);
  const isYesterday = date.toDateString() === dateYesterday.toDateString();
  const isThisYear = date.getUTCFullYear() === dateNow.getUTCFullYear();

  if (isToday) {
    return `${i18n.t('RelativeDate.Today')} ${i18n.t('RelativeDate.At')} ${formatDateTime(date, 'ShortTime')}`;
  }

  if (isYesterday) {
    return `${i18n.t('RelativeDate.Yesterday')} ${i18n.t('RelativeDate.At')} ${formatDateTime(date, 'ShortTime')}`;
  }

  if (isThisYear) {
    return `${formatDateTime(date, 'LongDateNoYear')} ${i18n.t('RelativeDate.At')} ${formatDateTime(date, 'ShortTime')}`;
  }

  return `${formatDateTime(date, 'LongDate')} ${i18n.t('RelativeDate.At')} ${formatDateTime(date, 'ShortTime')}`;
}

export function formatNumber(number: number, options?: Intl.NumberFormatOptions): string {
  const locale = i18n.language;
  return Intl.NumberFormat(locale, options).format(number);
}
